// Admin — Email Safety panel. Central hub for every outbound-email
// safety control: kill-switch, allowlist editor, test-send audit,
// per-digest previews, and bounce/complaint log.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Send, Mail, ShieldCheck, ShieldX, ServerCog,
  Plus, X, ChevronRight, AlertCircle, Undo2, FileText,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { MorningEmailConfigCard } from '../../components/custom/MorningEmailConfigCard';
import { YabbrPollStatusCard } from '../../components/custom/YabbrPollStatusCard';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const OUTCOME_TONE = {
  dispatched: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  'dropped:kill_switch': 'bg-rose-500/15 text-rose-200 border-rose-500/30',
  'dropped:allowlist:db': 'bg-amber-500/15 text-amber-200 border-amber-500/30',
  'dropped:allowlist:env': 'bg-amber-500/15 text-amber-200 border-amber-500/30',
};

const AdminEmailStatePage = () => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const authorised = user?.role === 'master_admin';

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testAddr, setTestAddr] = useState('');
  const [sending, setSending] = useState(false);

  // Allowlist editor state
  const [dbRules, setDbRules] = useState([]);
  const [newRule, setNewRule] = useState('');
  const [addingRule, setAddingRule] = useState(false);

  // Test log state
  const [testLog, setTestLog] = useState([]);

  // Bounces + audit rate state
  const [bounces, setBounces] = useState({ events: [], disabled: [] });
  const [bounceRate, setBounceRate] = useState(null);
  const [opsRecipients, setOpsRecipients] = useState([]);
  const toggleRecipient = async (r) => {
    try {
      await axios.patch(`${API}/admin/email-safety/recipients/${r.id}`, { enabled: !r.enabled }, { headers });
      setOpsRecipients((prev) => prev.map((x) => (x.id === r.id ? { ...x, enabled: !r.enabled } : x)));
      toast.success(`${r.email} will ${r.enabled ? 'no longer' : 'now'} receive ops emails`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update recipient');
    }
  };

  // Digest previews
  const [previewLoading, setPreviewLoading] = useState({});

  const fetchAll = useCallback(async () => {
    if (!token || !authorised) { setLoading(false); return; }
    setLoading(true);
    try {
      const [s, al, tl, bl, br, rc] = await Promise.all([
        axios.get(`${API}/admin/email-state`, { headers }),
        axios.get(`${API}/admin/email-allowlist`, { headers }),
        axios.get(`${API}/admin/email-state/test-log?limit=25`, { headers }),
        axios.get(`${API}/admin/email/bounces?limit=50`, { headers }),
        axios.get(`${API}/admin/email/bounce-rate?days=7`, { headers }),
        axios.get(`${API}/admin/email-safety/recipients`, { headers }),
      ]);
      setState(s.data);
      setDbRules(al.data.rules || []);
      setTestLog(tl.data.rows || []);
      setBounces(bl.data || { events: [], disabled: [] });
      setBounceRate(br.data || null);
      setOpsRecipients(rc.data.recipients || []);
      if (!testAddr && user?.email) setTestAddr(user.email);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load email state');
    } finally { setLoading(false); }
  }, [headers, token, authorised, testAddr, user?.email]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendTest = async () => {
    const to = (testAddr || '').trim();
    if (!to) { toast.error('Enter a recipient'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/email-state/test-send`, { to }, { headers });
      toast.success(`Test ${r.data.outcome === 'dispatched' ? 'delivered' : 'attempted'} to ${to}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally { setSending(false); }
  };

  const addRule = async () => {
    const r = (newRule || '').trim();
    if (!r) return;
    setAddingRule(true);
    try {
      await axios.post(`${API}/admin/email-allowlist`, { rule: r }, { headers });
      setNewRule('');
      toast.success('Rule added');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add rule');
    } finally { setAddingRule(false); }
  };

  const removeRule = async (rule) => {
    try {
      await axios.delete(`${API}/admin/email-allowlist/${encodeURIComponent(rule)}`, { headers });
      toast.success('Rule removed');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove rule');
    }
  };

  const sendPreview = async (kind, label) => {
    setPreviewLoading((p) => ({ ...p, [kind]: true }));
    try {
      const r = await axios.post(`${API}/admin/digest-preview/${kind}`, {}, { headers });
      toast.success(`${label} sent to ${r.data.sent_to}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} preview failed`);
    } finally {
      setPreviewLoading((p) => ({ ...p, [kind]: false }));
    }
  };

  const reEnable = async (email) => {
    try {
      await axios.post(`${API}/admin/email/disabled/${encodeURIComponent(email)}/re-enable`, {}, { headers });
      toast.success(`${email} re-enabled`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to re-enable');
    }
  };

  const [togglingKill, setTogglingKill] = useState(false);

  // Report recipients (shared with LLM Provider page endpoints)
  const [recipients, setRecipients] = useState(null);
  const [newRecipient, setNewRecipient] = useState('');
  const [recipBusy, setRecipBusy] = useState(false);
  const [overrideDrafts, setOverrideDrafts] = useState({});

  useEffect(() => {
    if (!authorised) return;
    axios.get(`${API}/admin/llm-costs/report-recipients`, { headers })
      .then((r) => {
        const d = {};
        Object.entries(r.data.overrides || {}).forEach(([k, v]) => { d[k] = (v || []).join(', '); });
        setOverrideDrafts(d);
      }).catch(() => {});
  }, [authorised, headers]);

  const saveOverrides = async () => {
    setRecipBusy(true);
    try {
      const payload = {};
      ['digest', 'spend_alert', 'provider_alert'].forEach((k) => {
        payload[k] = (overrideDrafts[k] || '').split(',').map((s) => s.trim()).filter(Boolean);
      });
      await axios.post(`${API}/admin/llm-costs/report-recipients`, payload, { headers });
      toast.success('Per-report recipient lists saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    setRecipBusy(false);
  };

  useEffect(() => {
    if (!authorised) return;
    axios.get(`${API}/admin/llm-provider/alert-recipients`, { headers })
      .then((r) => setRecipients(r.data)).catch(() => {});
  }, [authorised, headers]);

  const toggleAdminRecipient = async (email, excluded) => {
    setRecipBusy(true);
    try {
      const r = await axios.post(`${API}/admin/llm-provider/alert-recipients`, { email, excluded }, { headers });
      setRecipients(r.data);
      toast.success(excluded ? `${email} removed from reports` : `${email} re-added to reports`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update recipient'); }
    setRecipBusy(false);
  };

  const addCustomRecipient = async () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email) return;
    setRecipBusy(true);
    try {
      const r = await axios.post(`${API}/admin/llm-provider/alert-recipients/custom`, { email }, { headers });
      setRecipients(r.data);
      setNewRecipient('');
      toast.success(`${email} added to report recipients`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add recipient'); }
    setRecipBusy(false);
  };

  const removeCustomRecipient = async (email) => {
    setRecipBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/llm-provider/alert-recipients/custom/${encodeURIComponent(email)}`, { headers });
      setRecipients(r.data);
      toast.success(`${email} removed`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove recipient'); }
    setRecipBusy(false);
  };

  const toggleKillSwitch = async (nextEnabled) => {
    setTogglingKill(true);
    try {
      const r = await axios.patch(`${API}/admin/email-state/kill-switch`, {
        enabled: nextEnabled,
        revert_to_env: false,
      });
      toast.success(nextEnabled
        ? 'Emails are now ENABLED — live delivery resumed.'
        : 'Emails are now DISABLED — dry-run mode.');
      setState((prev) => ({ ...(prev || {}), ...r.data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to flip kill switch');
    } finally { setTogglingKill(false); }
  };
  const revertKillToEnv = async () => {
    setTogglingKill(true);
    try {
      const r = await axios.patch(`${API}/admin/email-state/kill-switch`, {
        enabled: false,   // ignored when revert_to_env=true
        revert_to_env: true,
      });
      toast.success(`Kill switch now follows the env fallback (${r.data.env_emails_enabled ? 'ENABLED' : 'DISABLED'}).`);
      setState((prev) => ({ ...(prev || {}), ...r.data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revert to env');
    } finally { setTogglingKill(false); }
  };

  const enabled = !!state?.emails_enabled;
  const killSource = state?.emails_enabled_source;
  const envEnabled = !!state?.env_emails_enabled;
  const allowlistActive = !!state?.allowlist_active;
  const source = state?.allowlist_source;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="email-state-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Email Safety</h1>
            <p className="text-white/40 text-sm mt-0.5 max-w-2xl">
              Central hub for every outbound email safety control — kill-switch, allowlist,
              test sends, digest previews, and bounce/complaint auto-suppression.
            </p>
          </div>
          <button
            onClick={fetchAll}
            className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-lg px-3 py-1.5"
            data-testid="email-state-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div>
        ) : !state ? (
          <p className="text-white/50 text-sm">No state loaded.</p>
        ) : (
          <>
            {/* Bounce Rate Alarm */}
            {bounceRate && (
              <div
                className={`rounded-xl border p-5 ${
                  bounceRate.alarming
                    ? 'bg-rose-500/15 border-rose-500/40'
                    : bounceRate.rate >= bounceRate.threshold * 0.6
                      ? 'bg-amber-500/10 border-amber-500/30'
                      : 'bg-emerald-500/5 border-emerald-500/20'
                }`}
                data-testid="bounce-rate-card"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className={`w-6 h-6 shrink-0 ${
                    bounceRate.alarming ? 'text-rose-300' : bounceRate.rate >= bounceRate.threshold * 0.6 ? 'text-amber-300' : 'text-emerald-300'
                  }`} />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-white/50">
                      Deliverability · {bounceRate.period_days}-day bounce rate
                    </div>
                    <div className={`text-lg font-bold mt-0.5 ${
                      bounceRate.alarming ? 'text-rose-200' : bounceRate.rate >= bounceRate.threshold * 0.6 ? 'text-amber-200' : 'text-emerald-200'
                    }`} data-testid="bounce-rate-value">
                      {(bounceRate.rate * 100).toFixed(2)}%
                      <span className="text-white/50 text-sm font-normal ml-2">
                        · {bounceRate.bounces.toLocaleString()} bounced / {bounceRate.sends.toLocaleString()} sent
                      </span>
                    </div>
                    <p className="text-xs text-white/60 mt-1">
                      {bounceRate.alarming
                        ? `Exceeds ${(bounceRate.threshold * 100).toFixed(0)}% threshold — sender reputation at risk. Investigate the suppression list below.`
                        : bounceRate.rate >= bounceRate.threshold * 0.6
                          ? `Approaching ${(bounceRate.threshold * 100).toFixed(0)}% threshold — watch closely.`
                          : `Well within the ${(bounceRate.threshold * 100).toFixed(0)}% threshold — deliverability healthy.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Layer 1 — kill switch */}
            <div className={`rounded-xl border p-5 ${enabled ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`} data-testid="email-state-kill-switch">
              <div className="flex items-center gap-3">
                {enabled ? <ShieldCheck className="w-6 h-6 text-emerald-300" /> : <ShieldX className="w-6 h-6 text-rose-300" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-white/50">Layer 1 · Kill switch</span>
                    {killSource && (
                      <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${killSource === 'db' ? 'bg-teal-500/20 text-teal-200' : 'bg-slate-500/20 text-slate-300'}`} data-testid="kill-switch-source">
                        source: {killSource}
                      </span>
                    )}
                  </div>
                  <div className={`text-lg font-bold ${enabled ? 'text-emerald-200' : 'text-rose-200'}`}>
                    Emails {enabled ? 'ENABLED — sending live' : 'DISABLED — dry-run mode'}
                  </div>
                  <p className="text-xs text-white/60 mt-1 max-w-xl">
                    {killSource === 'db'
                      ? <>Flipped from this panel. Env fallback would be <b>{envEnabled ? 'ENABLED' : 'DISABLED'}</b>. Click <b>Revert to env</b> to remove the DB override.</>
                      : <>Following the <code className="text-teal-300">EMAILS_ENABLED</code> env var. Toggle below to override without redeploying.</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Toggle switch — plain-language labels */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    onClick={() => toggleKillSwitch(!enabled)}
                    disabled={togglingKill}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400/70 disabled:opacity-50 ${enabled ? 'bg-emerald-500' : 'bg-rose-500'}`}
                    data-testid="kill-switch-toggle"
                    title={enabled ? 'Click to disable delivery' : 'Click to enable delivery'}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-8' : 'translate-x-1'}`} />
                  </button>
                  {killSource === 'db' && (
                    <button
                      type="button"
                      onClick={revertKillToEnv}
                      disabled={togglingKill}
                      className="text-[10px] font-semibold text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded px-2 py-1 uppercase tracking-wider disabled:opacity-40"
                      data-testid="kill-switch-revert"
                    >
                      Revert to env
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Layer 2 — allowlist editor */}
            <div className={`rounded-xl border p-5 ${allowlistActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-white/[0.02] border-white/10'}`} data-testid="email-state-allowlist">
              <div className="flex items-start gap-3">
                <ShieldCheck className={`w-6 h-6 shrink-0 mt-0.5 ${allowlistActive ? 'text-amber-300' : 'text-white/40'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-white/50">Layer 2 · Allowlist</span>
                    {source && (
                      <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${source === 'db' ? 'bg-teal-500/20 text-teal-200' : source === 'env' ? 'bg-slate-500/20 text-slate-300' : 'bg-white/10 text-white/50'}`}>
                        source: {source}
                      </span>
                    )}
                  </div>
                  <div className={`text-lg font-bold ${allowlistActive ? 'text-amber-200' : 'text-white/70'}`}>
                    {allowlistActive ? `${state.allowlist.length} rule(s) active` : 'No allowlist — every recipient allowed'}
                  </div>
                  <p className="text-xs text-white/60 mt-1 mb-3 max-w-xl">
                    Persist rules here to override the env fallback. Add exact addresses (<code className="text-teal-300">user@host.com</code>) or domain suffixes (<code className="text-teal-300">@host.com</code>).
                  </p>

                  {/* DB rule chips */}
                  {dbRules.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="email-state-db-rules">
                      {dbRules.map((r) => (
                        <span key={r.rule} className="inline-flex items-center gap-1 text-xs bg-teal-500/15 text-teal-100 border border-teal-500/40 rounded-full pl-2.5 pr-1 py-0.5 font-mono">
                          {r.rule}
                          <button onClick={() => removeRule(r.rule)} className="ml-1 hover:text-white p-0.5" data-testid={`allowlist-remove-${r.rule}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* env fallback chips (removable — removing one materialises the rest into DB rules) */}
                  {source === 'env' && dbRules.length === 0 && (state.allowlist || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="email-state-env-rules">
                      {state.allowlist.map((r) => (
                        <span key={r} className="inline-flex items-center gap-1 text-xs bg-slate-500/20 text-slate-200 border border-slate-500/40 rounded-full pl-2.5 pr-1 py-0.5 font-mono" title="From env fallback — removing it copies the remaining rules into editable DB rules">
                          {r} <span className="text-[9px] text-slate-400">env</span>
                          <button onClick={() => removeRule(r)} className="ml-1 hover:text-white p-0.5" data-testid={`allowlist-remove-env-${r}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* add rule form */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      value={newRule}
                      onChange={(e) => setNewRule(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addRule(); }}
                      placeholder="user@host.com or @host.com"
                      className="flex-1 min-w-[240px] bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-teal-500 font-mono"
                      data-testid="allowlist-add-input"
                    />
                    <button
                      onClick={addRule}
                      disabled={addingRule || !newRule.trim()}
                      className="inline-flex items-center gap-1.5 text-sm bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-white rounded-lg px-3 py-2 font-semibold"
                      data-testid="allowlist-add-btn"
                    >
                      {addingRule ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Add rule
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Ops email recipients */}
            <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid="email-state-recipients">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-white/50">Ops email recipients</span>
              <p className="text-xs text-white/60 mt-1 mb-3 max-w-xl">
                Which master admins receive operational emails — queue alerts, canary alerts, digests, demo-lead notifications and routing changes. Toggling off silences that person without touching the allowlist.
              </p>
              <div className="space-y-2">
                {opsRecipients.map((r) => (
                  <div key={r.id} className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2" data-testid={`ops-recipient-${r.email}`}>
                    <div>
                      <p className="text-sm text-white font-medium">{r.name || r.email}</p>
                      <p className="text-xs text-white/50 font-mono">{r.email}</p>
                    </div>
                    <button onClick={() => toggleRecipient(r)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${r.enabled ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'bg-white/5 text-white/40 border border-white/15'}`}
                      data-testid={`ops-recipient-toggle-${r.email}`}>
                      {r.enabled ? 'Receiving' : 'Muted'}
                    </button>
                  </div>
                ))}
                {opsRecipients.length === 0 && <p className="text-sm text-white/40">No master admins found.</p>}
              </div>
            </div>

            {/* SMTP + test send */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid="email-state-smtp">
                <div className="flex items-center gap-2 mb-3">
                  <ServerCog className="w-5 h-5 text-teal-300" />
                  <h2 className="text-white font-semibold">SMTP transport</h2>
                </div>
                <dl className="text-sm text-white/80 space-y-1">
                  <div><dt className="inline text-white/50">Host:</dt> <dd className="inline font-mono ml-1">{state.smtp_host}</dd></div>
                  <div><dt className="inline text-white/50">From:</dt> <dd className="inline font-mono ml-1">{state.smtp_from}</dd></div>
                  <div><dt className="inline text-white/50">Auth:</dt> <dd className={`inline font-semibold ml-1 ${state.smtp_configured ? 'text-emerald-300' : 'text-rose-300'}`}>{state.smtp_configured ? 'Configured' : 'Missing credentials'}</dd></div>
                </dl>
              </div>

              <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid="email-state-test">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-5 h-5 text-teal-300" />
                  <h2 className="text-white font-semibold">Send a test email</h2>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="email"
                    value={testAddr}
                    onChange={(e) => setTestAddr(e.target.value)}
                    placeholder="you@company.com"
                    className="flex-1 min-w-[220px] bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-teal-500"
                    data-testid="email-state-test-input"
                  />
                  <button
                    onClick={sendTest}
                    disabled={sending || !enabled}
                    className="inline-flex items-center gap-1.5 text-sm bg-teal-500 hover:bg-teal-400 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 font-semibold"
                    data-testid="email-state-test-send"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send test
                  </button>
                </div>
                {!enabled && (
                  <p className="text-xs text-rose-300 mt-2" data-testid="email-state-test-disabled-hint">
                    Kill switch is OFF — the send will be logged but not delivered.
                  </p>
                )}
              </div>
            </div>

            {/* Report recipients — who gets digests, spend alerts, provider alerts */}
            <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid="report-recipients-card">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-5 h-5 text-teal-300" />
                <h2 className="text-white font-semibold">Report recipients</h2>
              </div>
              <p className="text-xs text-white/50 mb-4 max-w-xl">
                Who receives the admin email reports — weekly cost digest, daily spend alerts, provider-failure
                alerts and routing-change notices. Toggle master admins off, or add any extra address.
                All sends still respect the allowlist above.
              </p>
              {recipients && (
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="report-recipients-list">
                  {recipients.recipients.map((r) => (
                    <span
                      key={r.email}
                      className={`inline-flex items-center gap-1 text-xs border rounded-full pl-2.5 pr-1 py-0.5 font-mono ${
                        r.excluded
                          ? 'bg-white/5 text-white/30 border-white/10 line-through'
                          : r.custom
                            ? 'bg-purple-500/15 text-purple-300 border-purple-500/40'
                            : 'bg-teal-500/15 text-teal-300 border-teal-500/40'
                      }`}
                      title={r.custom ? 'Custom recipient' : r.excluded ? 'Master admin — excluded from reports' : 'Master admin — receiving reports'}
                    >
                      {r.email}
                      {!r.custom && <span className="text-[9px] opacity-60 ml-0.5">admin</span>}
                      {r.custom ? (
                        <button onClick={() => removeCustomRecipient(r.email)} disabled={recipBusy} className="ml-1 hover:text-white p-0.5" data-testid={`recipient-remove-${r.email}`}>
                          <X className="w-3 h-3" />
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleAdminRecipient(r.email, !r.excluded)}
                          disabled={recipBusy}
                          className="ml-1 hover:text-white p-0.5 text-[9px] font-bold uppercase"
                          title={r.excluded ? 'Re-include in reports' : 'Exclude from reports'}
                          data-testid={`recipient-toggle-${r.email}`}
                        >
                          {r.excluded ? 'add' : <X className="w-3 h-3" />}
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="email"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCustomRecipient(); }}
                  placeholder="Add report recipient — anyone@company.com"
                  className="flex-1 min-w-[240px] bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-teal-500 font-mono"
                  data-testid="recipient-add-input"
                />
                <button
                  onClick={addCustomRecipient}
                  disabled={recipBusy || !newRecipient.trim()}
                  className="inline-flex items-center gap-1.5 text-sm bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-white rounded-lg px-3 py-2 font-semibold"
                  data-testid="recipient-add-btn"
                >
                  {recipBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add recipient
                </button>
              </div>
              {recipients && (
                <p className="text-[11px] text-white/40 mt-2" data-testid="report-recipients-effective">
                  Currently receiving reports: {recipients.effective.join(', ') || 'nobody'}
                </p>
              )}
              {/* per-report overrides */}
              <div className="mt-4 border-t border-white/10 pt-3 space-y-2" data-testid="per-report-overrides">
                <p className="text-xs font-semibold text-white/70">Per-report overrides <span className="text-white/40 font-normal">— blank = everyone above; otherwise ONLY these addresses get that report</span></p>
                {[['digest', 'Weekly cost digest'], ['spend_alert', 'Daily spend alert'], ['provider_alert', 'Provider-failure alert']].map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-white/50 w-44 shrink-0">{label}</span>
                    <input
                      type="text"
                      value={overrideDrafts[k] ?? ''}
                      onChange={(e) => setOverrideDrafts((p) => ({ ...p, [k]: e.target.value }))}
                      placeholder="comma-separated emails (blank = default)"
                      className="flex-1 min-w-[220px] bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-teal-500 font-mono"
                      data-testid={`override-input-${k}`}
                    />
                  </div>
                ))}
                <button
                  onClick={saveOverrides}
                  disabled={recipBusy}
                  className="inline-flex items-center gap-1.5 text-xs bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 font-semibold"
                  data-testid="override-save-btn"
                >
                  {recipBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Save per-report lists
                </button>
              </div>
            </div>

            {/* Digest previews */}
            <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid="digest-previews">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-teal-300" />
                <h2 className="text-white font-semibold">Digest previews</h2>
              </div>
              <p className="text-xs text-white/50 mb-4 max-w-xl">
                Send a preview copy of any digest to your own inbox — subject prefixed with <code className="text-teal-300">[PREVIEW]</code>. Uses the same HTML pipeline as the scheduled cron.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  { kind: 'manager', label: 'Manager weekly digest', desc: '7-day KPIs, top revivable deals, aging queue.' },
                  { kind: 'compliance', label: 'Compliance digest', desc: 'Governance breaches + score cells needing coaching.' },
                  { kind: 'weekly-coaching', label: 'Weekly Coaching report', desc: 'Team-wide coaching queue, heatmap, nudges.' },
                ].map((d) => (
                  <div key={d.kind} className="rounded-lg bg-black/20 border border-white/5 p-3" data-testid={`digest-preview-${d.kind}`}>
                    <div className="font-semibold text-white text-sm">{d.label}</div>
                    <p className="text-xs text-white/50 mt-1 mb-3 min-h-[32px]">{d.desc}</p>
                    <button
                      onClick={() => sendPreview(d.kind, d.label)}
                      disabled={!!previewLoading[d.kind]}
                      className="w-full inline-flex items-center justify-center gap-1.5 text-xs bg-teal-500/80 hover:bg-teal-400 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 font-semibold"
                      data-testid={`digest-preview-btn-${d.kind}`}
                    >
                      {previewLoading[d.kind] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Preview to me
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Test log */}
            <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid="test-log">
              <h2 className="text-white font-semibold flex items-center gap-2 mb-3">
                <ChevronRight className="w-4 h-4 text-teal-300" /> Test-send audit log
                <span className="text-xs font-normal text-white/40">last {testLog.length}</span>
              </h2>
              {testLog.length === 0 ? (
                <p className="text-white/40 text-xs italic">No test sends yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-white/40 border-b border-white/10">
                    <tr>
                      <th className="text-left py-1.5">When</th>
                      <th className="text-left py-1.5">By</th>
                      <th className="text-left py-1.5">To</th>
                      <th className="text-left py-1.5">Outcome</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {testLog.map((r) => (
                      <tr key={r.id} data-testid={`test-log-row-${r.id}`}>
                        <td className="py-2 text-white/60">{fmt(r.created_at)}</td>
                        <td className="py-2 text-white/80">{r.sent_by}</td>
                        <td className="py-2 text-white/80 font-mono">{r.to}</td>
                        <td className="py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${OUTCOME_TONE[r.outcome] || 'bg-white/10 text-white/60 border-white/20'}`}>
                            {r.outcome || 'unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Bounces + suppression */}
            <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid="bounces-card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-semibold flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-300" /> Bounces & complaints
                  <span className="text-xs font-normal text-white/40">
                    {bounces.disabled.length} suppressed · {bounces.events.length} events
                  </span>
                </h2>
              </div>
              <p className="text-xs text-white/50 mb-3 max-w-2xl">
                smtp2go posts bounce/complaint events to <code className="text-teal-300">/api/webhooks/smtp2go/bounce</code>.
                Addresses that hard-bounce or complain are auto-added to the suppression list and pulled from every digest fanout.
              </p>

              {bounces.disabled.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1">Suppressed addresses</div>
                  <div className="rounded-lg bg-black/20 border border-white/5 divide-y divide-white/5">
                    {bounces.disabled.map((d) => (
                      <div key={d.email} className="px-3 py-2 flex items-center justify-between text-xs" data-testid={`bounce-disabled-${d.email}`}>
                        <div className="min-w-0 flex-1">
                          <span className="font-mono text-white/90">{d.email}</span>
                          <span className="text-white/40 ml-2">{d.event}</span>
                          {d.reason && <span className="text-white/50 ml-2 italic">· {d.reason}</span>}
                        </div>
                        <button
                          onClick={() => reEnable(d.email)}
                          className="inline-flex items-center gap-1 text-teal-300 hover:text-teal-200"
                          data-testid={`bounce-reenable-${d.email}`}
                        >
                          <Undo2 className="w-3.5 h-3.5" /> Re-enable
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bounces.events.length === 0 ? (
                <p className="text-white/40 text-xs italic">No bounce or complaint events received yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-white/40 border-b border-white/10">
                    <tr>
                      <th className="text-left py-1.5">When</th>
                      <th className="text-left py-1.5">Event</th>
                      <th className="text-left py-1.5">Email</th>
                      <th className="text-left py-1.5">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {bounces.events.map((e) => (
                      <tr key={e.id}>
                        <td className="py-1.5 text-white/60">{fmt(e.received_at)}</td>
                        <td className="py-1.5 text-white/80">{e.event}</td>
                        <td className="py-1.5 font-mono text-white/80">{e.email}</td>
                        <td className="py-1.5 text-white/60 italic">{e.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
        <YabbrPollStatusCard />
        <MorningEmailConfigCard />
      </div>
    </AdminLayout>
  );
};

export default AdminEmailStatePage;
